import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import logoLeft from "../../assets/img/logo/new-logo.png";
import logoRight from "../../assets/img/logo/punjab-logo.png";
import { FFmpeg } from '@ffmpeg/ffmpeg';

const exportPagesToVideo = async () => {
  const pages = document.querySelectorAll(".pdf-page");
  if (!pages.length) {
    alert("No pages found. Please generate preview first.");
    return;
  }

  // Loading overlay
  let loadingOverlay = document.getElementById('loading-overlay');
  if (!loadingOverlay) {
    loadingOverlay = document.createElement('div');
    loadingOverlay.id = 'loading-overlay';
    Object.assign(loadingOverlay.style, {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0,0,0,0.8)',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '1.4rem',
      zIndex: 9999
    });

    const text = document.createElement('div');
    text.id = 'loading-text';
    text.innerText = 'Initializing...';
    text.style.marginBottom = '10px';

    const timeLeft = document.createElement('div');
    timeLeft.id = 'loading-time-left';
    timeLeft.innerText = '';
    timeLeft.style.fontSize = '1rem';
    timeLeft.style.marginTop = '4px';

    const barContainer = document.createElement('div');
    Object.assign(barContainer.style, {
      width: '60%',
      height: '16px',
      background: '#444',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: 'inset 0 0 5px rgba(0,0,0,0.4)'
    });

    const fill = document.createElement('div');
    fill.id = 'loading-bar';
    Object.assign(fill.style, {
      height: '100%',
      width: '0%',
      backgroundImage: 'linear-gradient(135deg, #00e676 25%, transparent 25%, transparent 50%, #00e676 50%, #00e676 75%, transparent 75%, transparent)',
      backgroundSize: '40px 40px',
      animation: 'progressStripe 1s linear infinite',
      transition: 'width 0.4s ease',
      borderRadius: '8px'
    });

    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      @keyframes progressStripe {
        from { background-position: 0 0; }
        to { background-position: 40px 0; }
      }
    `;
    document.head.appendChild(styleEl);

    barContainer.appendChild(fill);
    loadingOverlay.appendChild(text);
    loadingOverlay.appendChild(barContainer);
    loadingOverlay.appendChild(timeLeft);
    document.body.appendChild(loadingOverlay);
  } else {
    loadingOverlay.style.display = 'flex';
  }

  const updateProgress = (label, percent, estimatedTime = '') => {
    document.getElementById('loading-text').innerText = label;
    document.getElementById('loading-bar').style.width = `${percent}%`;
    document.getElementById('loading-time-left').innerText = estimatedTime ? `~${estimatedTime} left` : '';
  };

  const startTime = Date.now();

  const ffmpeg = new FFmpeg();
  await ffmpeg.load();

  updateProgress('Loading intro video...', 5);
  const introData = new Uint8Array(await (await fetch('../../assets/video/intro.mp4')).arrayBuffer());
  await ffmpeg.writeFile('intro.mp4', introData);

  updateProgress('Loading outro video...', 7);
  const outroData = new Uint8Array(await (await fetch('../../assets/video/outro.mp4')).arrayBuffer());
  await ffmpeg.writeFile('outro.mp4', outroData);

  const imageFiles = [];
  const durationPerImage = 10.5;
  const fadeDuration = 0.5;
  const outputWidth = 1920;
  const outputHeight = 1080;
  const fps = 25;

  for (let i = 0; i < pages.length; i++) {
    const estRemaining = ((Date.now() - startTime) / (i + 1)) * (pages.length - i - 1);
    const estMin = Math.floor(estRemaining / 60000);
    const estSec = Math.floor((estRemaining % 60000) / 1000);
    updateProgress(`Capturing page ${i + 1} of ${pages.length}`, (i / pages.length) * 30, `${estMin}m ${estSec}s`);

    const page = pages[i];
    page.style.display = "block";
    await new Promise(res => setTimeout(res, 150)); // Slightly reduced wait

    const canvas = await html2canvas(page, { scale: 3 }); // Slightly lower scale = faster
    const canvasAspect = canvas.width / canvas.height;
    const outputAspect = outputWidth / outputHeight;

    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = outputWidth;
    resizedCanvas.height = outputHeight;
    const ctx = resizedCanvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, outputWidth, outputHeight);

    let drawWidth, drawHeight, offsetX, offsetY;
    if (canvasAspect > outputAspect) {
      drawWidth = outputWidth;
      drawHeight = outputWidth / canvasAspect;
      offsetX = 0;
      offsetY = (outputHeight - drawHeight) / 2;
    } else {
      drawHeight = outputHeight;
      drawWidth = outputHeight * canvasAspect;
      offsetX = (outputWidth - drawWidth) / 2;
      offsetY = 0;
    }

    ctx.drawImage(canvas, offsetX, offsetY, drawWidth, drawHeight);

    // Add page label
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(outputWidth - 160, outputHeight - 50, 150, 35);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`Page ${i + 1} of ${pages.length}`, outputWidth - 10, outputHeight - 20);

    const imgData = resizedCanvas.toDataURL("image/png");
    const byteString = atob(imgData.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let j = 0; j < byteString.length; j++) ia[j] = byteString.charCodeAt(j);

    const filename = `page${i + 1}.png`;
    await ffmpeg.writeFile(filename, ia);
    imageFiles.push(filename);
  }

 for (let i = 0; i < imageFiles.length; i++) {
  const estRemaining = ((Date.now() - startTime) / (i + 1)) * (imageFiles.length - i - 1);
  const estMin = Math.floor(estRemaining / 60000);
  const estSec = Math.floor((estRemaining % 60000) / 1000);
  updateProgress(`Encoding page video ${i + 1}`, 30 + (i / imageFiles.length) * 40, `${estMin}m ${estSec}s`);

  const input = imageFiles[i];
  const output = `video${i + 1}.mp4`;

  const fadeIn = `fade=t=in:st=0:d=${fadeDuration}`;
  const fadeOut = i === imageFiles.length - 1
    ? ''  // No fade out on last page
    : `fade=t=out:st=${(durationPerImage - fadeDuration).toFixed(2)}:d=${fadeDuration}`;

  const vfFilter = [fadeIn, fadeOut].filter(Boolean).join(',');

  await ffmpeg.exec([
    '-loop', '1',
    '-i', input,
    '-t', `${durationPerImage}`,
    '-vf', vfFilter,
    '-pix_fmt', 'yuv420p',
    '-r', `${fps}`,
    '-c:v', 'libx264',
    '-preset', 'ultrafast',
    '-crf', '23',
    output
  ]);
}

  const videoFiles = ['intro.mp4', ...imageFiles.map((_, i) => `video${i + 1}.mp4`), 'outro.mp4'];
  const videoListFile = videoFiles.map(file => `file '${file}'`).join('\n');
  await ffmpeg.writeFile('video_input.txt', new TextEncoder().encode(videoListFile));

  updateProgress('Concatenating final video...', 90);
  await ffmpeg.exec([
    '-f', 'concat',
    '-safe', '0',
    '-i', 'video_input.txt',
    '-c:v', 'libx264',
    '-preset', 'ultrafast',
    '-crf', '22',
    'out.mp4'
  ]);

  updateProgress('Preparing download...', 98);
  const data = await ffmpeg.readFile('out.mp4');
  const videoBlob = new Blob([data.buffer], { type: 'video/mp4' });
  const videoUrl = URL.createObjectURL(videoBlob);
  loadingOverlay.style.display = 'none';

  const filterDateValue = document.getElementById('filter-date')?.value;
  let selectedDate = filterDateValue ? new Date(filterDateValue) : new Date();
  if (isNaN(selectedDate)) selectedDate = new Date();
  const pad = (n) => n.toString().padStart(2, '0');
  const datetimeStr = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}`;

  const a = document.createElement('a');
  a.href = videoUrl;
  a.download = `CSW_Ward_${datetimeStr}.mp4`;
  a.click();
};

const CswRoasterCombinedPDF = () => {
  const pdfRef = useRef();
  const buttonRef = useRef();
  const [filterDate, setFilterDate] = useState("");
  const [readableDate, setReadableDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const shiftOrder = ["Morning", "Evening", "Night"];
  const roleOrder = ["Doctor", "Nurse", "Ward Boy / Aya", "Janitorial Staff"];



    const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  useEffect(() => {
    if (filterDate) {
      setCurrentPage(1);
      fetchAndTranslate(filterDate);
      const formatted = new Date(filterDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      setReadableDate(formatted);
    }
  }, [filterDate]);

  const fetchAndTranslate = async (date) => {
    setLoading(true);
    try {
      const res = await axios.get(`https://ricgop.online/backend/getCswByDate.php?date=${date}`);
      if (Array.isArray(res.data)) {
        const translated = await Promise.all(
          res.data.map(async (entry) => {
            const urduDay = await translate(entry.day);
            const urduRole = await translate(entry.role);
            const urduShift = await translate(entry.shift);
            const urduName = await translate(entry.name);
            return { ...entry, urduDay, urduRole, urduShift, urduName };
          })
        );

        const sorted = translated
          .sort((a, b) => {
            const shiftDiff = shiftOrder.indexOf(a.shift) - shiftOrder.indexOf(b.shift);
            if (shiftDiff !== 0) return shiftDiff;
            return roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role);
          })
          .map((entry, idx) => ({ ...entry, no: idx + 1 }));

        setData(sorted);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const translate = async (text) => {
    if (!text) return "";
    try {
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(text)}`
      );
      const json = await res.json();
      return json?.[0]?.map((part) => part[0]).join(" ") || text;
    } catch {
      return text;
    }
  };

const generateGroupedPreviews = () => {
  const container = document.getElementById("pdf-preview-container");
  container.innerHTML = "";

  const shiftOrder = ["Morning", "Evening", "Night"];
  const shiftLabels = {
    Morning: { urdu: "نام (مارننگ)", en: "Names (Morning)" },
    Evening: { urdu: "نام (ایوننگ)", en: "Names (Evening)" },
    Night: { urdu: "نام (نائٹ)", en: "Names (Night)" }
  };

  const createGroupSection = (roleKey, filterKey, groupTitleEn, groupTitleUrdu) => {
    const groupDataExists = data.some(d => d.role.toLowerCase().includes(filterKey));
    if (!groupDataExists) return;

    const sectionPage = document.createElement("div");
    sectionPage.className = "pdf-page";
    sectionPage.style.width = "297mm";
    sectionPage.style.minHeight = "210mm";
    sectionPage.style.margin = "0 auto";
    sectionPage.style.background = "#fff";
    sectionPage.style.position = "relative";
    sectionPage.style.fontFamily = "'Segoe UI', 'Jameel Noori Nastaleeq', serif";
    sectionPage.style.display = "flex";
    sectionPage.style.flexDirection = "column";
    sectionPage.style.justifyContent = "space-between";
    sectionPage.style.padding = "0";

    // Watermark
    const watermark = document.createElement("img");
    watermark.src = logoLeft;
    watermark.style.position = "absolute";
    watermark.style.top = "50%";
    watermark.style.left = "50%";
    watermark.style.transform = "translate(-50%, -50%)";
    watermark.style.opacity = "0.015";
    watermark.style.width = "550px";
    watermark.style.zIndex = "0";
    sectionPage.appendChild(watermark);

    // Header
    const header = document.createElement("div");
    header.style.padding = "20px 40px 10px";
    header.style.position = "relative";
    header.style.zIndex = "2";
    header.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <img src="${logoLeft}" style="height: 85px;" />
        <div style="text-align: center; flex-grow: 1;">
          <h2 style="margin: 0; font-size: 30px;">Rawalpindi Institute of Cardiology (RIC)</h2>
          <h4 style="margin: 0; font-size: 22px;">Cardiac Surgery Ward Roster</h4>
        </div>
        <img src="${logoRight}" style="height: 85px;" />
      </div>
      <h4 style="text-align:center; margin-top: 12px; color: #333; background:#f1f1f1; padding:12px 24px; border-radius:6px; font-size: 22px;">📋 ${groupTitleEn} / ${groupTitleUrdu} - ${readableDate}</h4>
    `;
    sectionPage.appendChild(header);

    // Content Wrapper (to allow pushing footer down)
    const contentWrapper = document.createElement("div");
    contentWrapper.style.flex = "1";
    contentWrapper.style.display = "flex";
    contentWrapper.style.flexDirection = "column";
    contentWrapper.style.justifyContent = "flex-start";
    contentWrapper.style.padding = "20px 40px";

    // Grid Container
    const groupGrid = document.createElement("div");
    groupGrid.style.display = "grid";
    groupGrid.style.gridTemplateColumns = `repeat(${shiftOrder.length}, 1fr)`;
    groupGrid.style.gap = "18px";
    groupGrid.style.marginTop = "20px";
    groupGrid.style.position = "relative";
    groupGrid.style.zIndex = "2";

    shiftOrder.forEach((shift) => {
      const group = data.filter(d => d.role.toLowerCase().includes(filterKey) && d.shift === shift);
      if (!group.length) return;

      const block = document.createElement("div");
      block.style.padding = "20px";
      block.style.border = "1px solid #cfd9e0";
      block.style.borderRadius = "12px";
      block.style.background = "#f4f9ff";
      block.style.boxShadow = "0 2px 6px rgba(0,0,0,0.04)";

      const heading = document.createElement("div");
      heading.style.display = "flex";
      heading.style.justifyContent = "space-between";
      heading.style.alignItems = "center";
      heading.style.color = "#0d6efd";
      heading.style.borderBottom = "1px solid #b4d4fc";
      heading.style.paddingBottom = "8px";
      heading.style.marginBottom = "14px";
      heading.style.fontSize = "18px";

      const title = document.createElement("div");
      title.style.textAlign = "right";
      title.innerHTML = `
        <span style="direction: rtl; font-family: 'Jameel Noori Nastaleeq', serif;">${shiftLabels[shift].urdu}</span>
        <span style="direction: ltr;"> / ${shiftLabels[shift].en}</span>
      `;

      const count = document.createElement("span");
      count.innerText = `(${group.length})`;
      count.style.fontSize = "14px";
      count.style.color = "#666";
      count.style.marginLeft = "10px";

      heading.appendChild(title);
      heading.appendChild(count);
      block.appendChild(heading);

      group.forEach((person, idx) => {
        const entry = document.createElement("div");
        entry.style.fontSize = "17px";
        entry.style.marginBottom = "7px";
        entry.style.padding = "6px 14px";
        entry.style.background = idx % 2 === 0 ? "#ffffff" : "#eaf1f9";
        entry.style.borderRadius = "5px";
        entry.style.display = "flex";
        entry.style.justifyContent = "space-between";
        entry.style.flexDirection = "row-reverse";
        entry.innerHTML = `
          <strong style="font-family: 'Jameel Noori Nastaleeq', serif;">${person.urduName}</strong>
          <span style="color:gray; direction: ltr; padding-left: 14px;">${person.name}</span>
        `;
        block.appendChild(entry);
      });

      groupGrid.appendChild(block);
    });

    contentWrapper.appendChild(groupGrid);
    sectionPage.appendChild(contentWrapper);

    // Footer
    const footer = document.createElement("div");
    footer.style.textAlign = "center";
    footer.style.fontSize = "13px";
    footer.style.color = "#999";
    footer.style.padding = "14px 0";
    footer.style.borderTop = "1px solid #ccc";
    footer.style.position = "relative";
    footer.style.zIndex = "2";
    sectionPage.appendChild(footer);
    footer.innerText = "This roster is generated by RIC System • www.ricgop.online";

    container.appendChild(sectionPage);
  };

  createGroupSection("doctor", "doctor", "Doctors", "ڈاکٹر");
  createGroupSection("nurse", "nurse", "Nurses", "نرسیں");
  createGroupSection("ward", "ward boy", "Ward Boy / Aya", "وارڈ بوائے / آیا");
  createGroupSection("janitor", "janitor", "Janitorial Staff", "صفائی عملہ");
};

const downloadGroupedPDF = async () => {
  const pages = document.querySelectorAll(".pdf-page");

  if (!pages.length) {
    alert("No content to export. Please generate preview first.");
    return;
  }

  const pdf = new jsPDF("landscape", "mm", "a4");
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];

    // Hide all others temporarily
    pages.forEach((el, index) => {
      el.style.display = index === i ? "block" : "none";
    });

    // Wait for the DOM to re-render
    await new Promise((res) => setTimeout(res, 300));

    const canvas = await html2canvas(page, { scale: 2 });
    const imgData = canvas.toDataURL("image/jpeg", 0.8); // Compressed

    const imgProps = pdf.getImageProperties(imgData);
    const pxToMm = (px) => px * 25.4 / 96;
    const imgW = pxToMm(imgProps.width);
    const imgH = pxToMm(imgProps.height);
    const scale = Math.min((width - 20) / imgW, (height - 30) / imgH);
    const finalW = imgW * scale;
    const finalH = imgH * scale;
    const x = (width - finalW) / 2;
    const y = 20;

    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "JPEG", x, y, finalW, finalH);
  }

  // Reset all sections visible after export
  pages.forEach((el) => (el.style.display = "block"));

  pdf.setFontSize(10);
  pdf.setTextColor(100);
  pdf.text("Generated by RIC Hospital System", width / 2, height - 10, { align: "center" });

  pdf.save(`CSW_Grouped_Roaster_${filterDate}.pdf`);
};


  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = data.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  return (

    
 <div>
    {/* Modal for Preview */}
{/* Modal for Preview */}
      <div
        className="modal fade"
        id="groupedPdfModal"
        tabIndex="-1"
        aria-labelledby="groupedPdfModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div
            className="modal-content"
            style={{
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div className="modal-header">
              <h5 className="modal-title" id="groupedPdfModalLabel">
                📘 Grouped PDF Preview
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div
              className="modal-body"
              style={{ overflowY: "auto", flex: 1 }}
            >
              <div
                id="pdf-preview-container"
                style={{
                  background: "#fff",
                  padding: "40px",
                  fontFamily: "'Segoe UI', 'Jameel Noori Nastaleeq', serif",
                  width: "100%",
                  maxWidth: "800px",
                  margin: "auto",
                }}
              >
                {/* Example: Show only the current page */}
                {Array.from({ length: totalPages }, (_, i) => (
                  <div
                    key={i}
                    className="pdf-page"
                    style={{
                      display: currentPage === i + 1 ? "block" : "none"
                    }}
                  >
                    <h4>Page {i + 1}</h4>
                    {/* Replace with actual content */}
                    <p>This is content for page {i + 1}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={downloadGroupedPDF}>
                📄 Export PDF
              </button>
              <button
  className="btn btn-danger px-4"
  onClick={exportPagesToVideo}
  disabled={!data.length}
>
  🎬 Export as Video (MP4)
</button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    <div
      id="pdf-preview-container"
      style={{
        display: "none",
        background: "#fff",
        padding: "40px",
        fontFamily: "'Segoe UI', 'Jameel Noori Nastaleeq', serif",
        width: "800px",
        margin: "auto",
      }}
    ></div>
    <div style={{ padding: 30, background: "#eef3f9" }}>
      <h4 className="text-center mb-4 text-primary fw-bold">📋 CSW Roaster Export</h4>

      <div className="d-flex justify-content-center gap-3 mb-4">
        <input
          type="date"
          className="form-control w-auto"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
<button
  className="btn btn-warning px-4"
  onClick={generateGroupedPreviews}
  disabled={!data.length}
  data-bs-toggle="modal"
  data-bs-target="#groupedPdfModal"
>
  📘 Preview Grouped PDF
</button>
      </div>

      {loading && <p className="text-center text-muted">Please wait... Translating data</p>}

      <div
        ref={pdfRef}
        style={{
          backgroundColor: "#fff",
          minWidth: "1100px",
          padding: "40px 50px",
          borderRadius: "12px",
          fontFamily: "'Segoe UI', 'Jameel Noori Nastaleeq', serif",
          boxShadow: "0 0 15px rgba(0,0,0,0.1)",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
          <img src={logoLeft} alt="left-logo" style={{ height: 80 }} />
          <h2 className="text-center flex-grow-1 text-dark fw-bold mb-0">
            Rawalpindi Institute of Cardiology (RIC)
          </h2>
          
          <img src={logoRight} alt="right-logo" style={{ height: 80 }} />
        </div>

        <h5 className="text-center text-secondary mb-4">
          🗓️ Roaster Date: <span className="text-dark">{readableDate}</span>
        </h5>

        <table
          className="table text-center table-bordered"
          style={{
            fontSize: "15px",
            borderColor: "#ccc",
            borderCollapse: "collapse",
          }}
        >
          <thead style={{ backgroundColor: "#dbeeff", fontWeight: "600" }}>
            <tr>
              <th>#</th>
              <th>Day<br /><span className="fw-normal">دن</span></th>
              <th>Role<br /><span className="fw-normal">عہدہ</span></th>
              <th>Shift<br /><span className="fw-normal">شفٹ</span></th>
              <th>Name<br /><span className="fw-normal">نام</span></th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((d, i) => (
              <tr key={d.no} style={{ backgroundColor: i % 2 === 0 ? "#f9fafd" : "#ffffff" }}>
                <td>{d.no}</td>
                <td>
                  {d.day}
                  <br />
                  <strong style={{ fontFamily: "'Jameel Noori Nastaleeq'" }}>{d.urduDay}</strong>
                </td>
                <td>
                  {d.role}
                  <br />
                  <strong style={{ fontFamily: "'Jameel Noori Nastaleeq'" }}>{d.urduRole}</strong>
                </td>
                <td>
                  {d.shift}
                  <br />
                  <strong style={{ fontFamily: "'Jameel Noori Nastaleeq'" }}>{d.urduShift}</strong>
                </td>
                <td>
                  {d.name}
                  <br />
                  <strong style={{ fontFamily: "'Jameel Noori Nastaleeq'" }}>{d.urduName}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3 gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              ⬅️ Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentPage(idx + 1)}
                className={`btn btn-sm ${currentPage === idx + 1 ? "btn-primary" : "btn-outline-secondary"}`}
              >
                {idx + 1}
              </button>
            ))}
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next ➡️
            </button>
          </div>
        )}

        <div className="text-center mt-5 text-muted fs-6 fst-italic">
          This roaster is for RIC
        </div>
      </div>
    </div>
    </div>
  );
};

export default CswRoasterCombinedPDF;
